import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Loader,
  Notification,
  Message,
} from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { DisplayGuid, DisplayDate } from "../items";
import { DetailsRightBar, Divider, Action } from "./DetailsRightBar";

const FacilityRedirectInternal = (props) => {
  const [redirectData, setRedirectData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const { match: { params: { redirectId } } } = props;
  const { api: { facilityRedirects: { getFacilityRedirect, deleteFacilityRedirect, toggleActiveFacilityRedirect } } } = props;

  useEffect(() => {
    const loadRedirect = async () => {
      try {
        const redirect = await getFacilityRedirect(redirectId);
        setRedirectData(redirect);
      } catch (e) {
        setErrorMessage(new Message('error', 'Unable to get facility redirect data'));
      }
    };

    loadRedirect();
  }, [redirectId, getFacilityRedirect]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this redirect? This action cannot be undone. Remember to restart the application after deletion.')) {
      return;
    }

    try {
      await deleteFacilityRedirect(redirectId);
      setErrorMessage(new Message('success', 'Redirect deleted successfully. Remember to restart the application.'));
      setTimeout(() => {
        props.history.push('/facility-redirects');
      }, 2000);
    } catch (e) {
      const message = e.response?.data?.message || 'Unable to delete redirect';
      setErrorMessage(new Message('error', message));
    }
  };

  const handleToggleActive = async () => {
    const action = redirectData.isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this redirect? Remember to restart the application after this change.`)) {
      return;
    }

    try {
      const updatedRedirect = await toggleActiveFacilityRedirect(redirectId);
      setRedirectData(updatedRedirect);
      setErrorMessage(new Message('success', `Redirect ${action}d successfully. Remember to restart the application.`));
    } catch (e) {
      const message = e.response?.data?.message || `Unable to ${action} redirect`;
      setErrorMessage(new Message('error', message));
    }
  };

  const handleEdit = () => {
    props.history.push(`/facility-redirect/${redirectId}/edit`);
  };

  const getRedirectTypeLabel = (type) => {
    switch(type) {
      case 'FacilitySlugRename':
        return 'Facility Slug Rename';
      case 'ProductSlugRename':
        return 'Product Slug Rename';
      case 'FullUrlRedirect':
        return 'Full URL Redirect';
      default:
        return type;
    }
  };

  let mappedData = null;

  if (redirectData) {
    const {
      id,
      sourceSlug,
      targetSlug,
      targetUrl,
      type,
      productSlug,
      createdAt,
      updatedAt,
      isActive
    } = redirectData;

    mappedData = {
      'Id': <DisplayGuid>{id}</DisplayGuid>,
      'Source Slug': sourceSlug,
      'Target Slug': targetSlug || '-',
      'Target URL': targetUrl || '-',
      'Type': getRedirectTypeLabel(type),
      'Product Slug': productSlug || '-',
      'Status': (
        <span style={{ color: isActive ? 'green' : 'red', fontWeight: 'bold' }}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      'Created At (UTC)': <DisplayDate>{createdAt}</DisplayDate>,
      'Updated At (UTC)': updatedAt ? <DisplayDate>{updatedAt}</DisplayDate> : '-',
    };
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={errorMessage} />

          <div className='content-with-right-pane-delimiter'>
            <PageTop
              documentTitle='Facility Redirect Details'
              documentDescription='Facility redirect details'
            />

            <h2 className='main-page-header main-page-header-with-subheader'>
              Facility Redirect
            </h2>
            <h5 className='main-page-subheader'>{redirectId}</h5>
            <br />

            {
              mappedData ? (
                <React.Fragment>
                  <DataDefinitionList
                    listData={mappedData}
                    classNames={['detail-page-list']}
                  />
                </React.Fragment>
              ) : <Loader hasLoaded={false}>Loading...</Loader>
            }
          </div>

          <DetailsRightBar>
            {
              mappedData ? (
                <React.Fragment>
                  <Divider>Actions</Divider>
                  <Action action={handleEdit}>
                    Edit Redirect
                  </Action>
                  <Action action={handleToggleActive}>
                    {redirectData.isActive ? 'Deactivate' : 'Activate'} Redirect
                  </Action>
                  <Action action={handleDelete}>
                    Delete Redirect
                  </Action>
                </React.Fragment>
              ) : ''
            }
          </DetailsRightBar>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

FacilityRedirectInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

const FacilityRedirect = withRouter(withApi(connect(({ user }) => ({ isLoggedIn: !!user }), {})(FacilityRedirectInternal)));

FacilityRedirect.requiresLogin = true;
FacilityRedirect.propTypes = { isLoggedIn: PropTypes.bool };

export default FacilityRedirect;
